import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AsyncPipe} from "@angular/common";
import {combineLatest, map, Observable, of, timer} from "rxjs";
import {GameInstanceService} from "../../services/game-instance.service";
import {GameService} from "../../services/game.service";
import {IGameQueryResult} from "../../interfaces/IGameQueryResult";
import {IActiveGameInstance} from "../../interfaces/IActiveGameInstance";
import {PermissionTypes} from "../../enum/PermissionTypes";
import {EndGameInstanceComponent} from "../../components/dialogs/end-game-instance/end-game-instance.component";
import {MatDialog} from "@angular/material/dialog";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {MatMiniFabButton} from "@angular/material/button";
import {MatTooltip} from "@angular/material/tooltip";
import {
  DisplayActiveGameInstanceComponent
} from "../../components/display-active-game-instance/display-active-game-instance.component";
import {MatIcon} from "@angular/material/icon";
import {
  UserHasCausableAdminAccessDirective
} from "../../directives/permissions/user-has-causable-admin-access.directive";
import {HasPermissionDirective} from "../../directives/permissions/has-permission";
import {ContainerComponent} from "@coreui/angular";
import {ActiveUserService} from "../../services/active-user.service";
import {SnackbarService} from "../../services/snackbar.service";
import {GameInstanceStatuses} from "../../enum/GameInstanceStatusEnum";
import {GameStateEnum} from "../../enum/GameStateEnum";
import {CountdownService} from "../../services/countdown.service";
import {PipesModule} from "../../pipes/pipes.module";

@Component({
  selector: 'app-active-game-instances',
  standalone: true,
  templateUrl: './active-game-instances.component.html',
  imports: [
    MatMenu,
    MatIcon,
    MatMenuItem,
    MatMiniFabButton,
    MatMenuTrigger,
    MatTooltip,
    DisplayActiveGameInstanceComponent,
    UserHasCausableAdminAccessDirective,
    HasPermissionDirective,
    ContainerComponent,
    AsyncPipe,
    PipesModule
  ],
  styleUrls: ['./active-game-instances.component.scss']
})
export class ActiveGameInstancesComponent implements OnInit {
  public activeGame: IGameQueryResult | undefined = this.gameService.activeGame();
  public chosenGamesActiveDraw: IActiveGameInstance | null = null;
  public nextDraw$: Observable<string> = of('');
  private countdownService = inject(CountdownService);
  private destroyRef = inject(DestroyRef);
  protected readonly PermissionTypes = PermissionTypes;

  constructor(private gameInstanceService: GameInstanceService,
              private matDialog: MatDialog,
              private snackBarService: SnackbarService,
              private activeUserService: ActiveUserService,
              private gameService: GameService) {
  }

  protected readonly permissionTypes = PermissionTypes;

  ngOnInit(): void {
    this.gameInstanceService.activeGameInstanceByGame(this.activeGame?.Id!, this.activeUserService.activeUser().Id).subscribe({
      next: (activeGameInstanceP: IActiveGameInstance) => {
        this.chosenGamesActiveDraw = activeGameInstanceP;
        this.nextDraw$ = combineLatest([this.gameService.selectActiveGame$(), timer(0, 1000)]).pipe(
          map(([gameP]) => this.nextDrawText(gameP?.State, activeGameInstanceP)),
          takeUntilDestroyed(this.destroyRef));
      },
      error: (err) => {
        if (err?.Error?.status === 404) {
          this.chosenGamesActiveDraw = null;
          this.snackBarService.openErrorSnackBar('Looks like there is no active game instance for this game');
        } else {
          this.snackBarService.openErrorSnackBar('There was an error retrieving the active game instance for this game. Please try again or contact a system admin');
        }
      }
    })
  }

  // A paused or unlicensed game won't be drawn at its end time, so the banner says why instead of counting down.
  private nextDrawText(gameStateP: GameStateEnum | undefined, drawP: IActiveGameInstance): string {
    if (gameStateP === GameStateEnum.Paused) {
      return 'Paused';
    }

    if (gameStateP === GameStateEnum.NoLicense) {
      return 'No licence';
    }

    if (drawP.State === GameInstanceStatuses.ReadyForDraw) {
      return 'Ready for Draw';
    }

    return this.countdownService.formatRemainingTimeUntilDraw(new Date(drawP.EndedOn));
  }

  openEndGameInstance() {
    this.matDialog.open(EndGameInstanceComponent, {
      data: this.gameInstanceService.activeGameInstance(),
      disableClose: true
    })
  }
}
